import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import type { SitePlanViewSlide } from './sitePlanViews';

const SWIPE_THRESHOLD_PX = 40;
const SLIDER_HEIGHT = 'min(600px, 65vh)';

/** Site-plan "Views" mode: one zoomable image at a time with arrows, swipe, keys and dots. */
export default function SitePlanViewsSlider({ slides }: { slides: readonly SitePlanViewSlide[] }) {
  const [index, setIndex] = React.useState(0);
  const swipeStartX = React.useRef<number | null>(null);
  // Swipe only flips slides at 1x; once zoomed, a drag pans the image instead.
  const zoomedRef = React.useRef(false);
  const count = slides.length;

  const go = React.useCallback(
    (delta: number) => setIndex((current) => (current + delta + count) % count),
    [count],
  );

  React.useEffect(() => {
    setIndex(0);
  }, [slides]);

  // Warm the neighbours so stepping through doesn't flash blank.
  React.useEffect(() => {
    if (count < 2) return;
    [index - 1, index + 1].forEach((i) => {
      const img = new Image();
      img.src = slides[(i + count) % count].url;
    });
  }, [index, slides, count]);

  if (count === 0) return null;
  const slide = slides[index];

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    bgcolor: 'rgb(0 0 0 / 0.45)',
    color: '#fff',
    '&:hover': { bgcolor: 'rgb(0 0 0 / 0.65)' },
    zIndex: 1,
  } as const;
  const zoomButtonSx = {
    bgcolor: 'rgb(0 0 0 / 0.45)',
    color: '#fff',
    '&:hover': { bgcolor: 'rgb(0 0 0 / 0.65)' },
  } as const;

  return (
    <Box
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') go(-1);
        if (event.key === 'ArrowRight') go(1);
      }}
      sx={{ outline: 'none' }}
    >
      <Box
        onPointerDown={(event) => {
          swipeStartX.current = event.clientX;
        }}
        onPointerUp={(event) => {
          const start = swipeStartX.current;
          swipeStartX.current = null;
          if (start === null || zoomedRef.current) return;
          const dx = event.clientX - start;
          if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) go(dx < 0 ? 1 : -1);
        }}
        onPointerCancel={() => {
          swipeStartX.current = null;
        }}
        sx={{
          position: 'relative',
          height: SLIDER_HEIGHT,
          border: '1px solid rgb(var(--color-text-rgb) / 0.12)',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgb(var(--color-text-rgb) / 0.03)',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {/* Keyed per slide so each image opens at 1x. */}
        <TransformWrapper
          key={slide.url}
          initialScale={1}
          minScale={1}
          maxScale={5}
          centerOnInit
          limitToBounds
          wheel={{ step: 0.1 }}
          doubleClick={{ step: 0.7 }}
          panning={{ velocityDisabled: false }}
          onInit={() => {
            zoomedRef.current = false;
          }}
          onTransformed={(_, state) => {
            zoomedRef.current = state.scale > 1.01;
          }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Box
                  component="img"
                  data-magnify-image="site-plan-view"
                  src={slide.url}
                  alt={slide.label}
                  draggable={false}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: SLIDER_HEIGHT,
                    objectFit: 'contain',
                    display: 'block',
                    '@keyframes sitePlanViewFade': { from: { opacity: 0 }, to: { opacity: 1 } },
                    animation: 'sitePlanViewFade 0.25s ease',
                  }}
                />
              </TransformComponent>
              <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
                <IconButton size="small" aria-label="Zoom in" onClick={() => zoomIn()} sx={zoomButtonSx}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Zoom out" onClick={() => zoomOut()} sx={zoomButtonSx}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Reset zoom" onClick={() => resetTransform()} sx={zoomButtonSx}>
                  <ResetZoomIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}
        </TransformWrapper>
        {count > 1 && (
          <>
            <IconButton aria-label="Previous view" onClick={() => go(-1)} sx={{ ...arrowSx, left: 8 }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton aria-label="Next view" onClick={() => go(1)} sx={{ ...arrowSx, right: 8 }}>
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'rgb(0 0 0 / 0.55)',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {index + 1} / {count}
        </Typography>
      </Box>
      {count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.25 }}>
          {slides.map((s, i) => (
            <Box
              key={s.url}
              component="button"
              type="button"
              aria-label={s.label}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              sx={{
                width: i === index ? 20 : 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: 4,
                cursor: 'pointer',
                bgcolor: i === index ? 'var(--color-accent)' : 'rgb(var(--color-text-rgb) / 0.25)',
                transition: 'width 0.2s ease, background-color 0.2s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
